import { Button, Input, Modal, Select } from '@convergence/components';
import Editor from '@monaco-editor/react';
import { Case, Switch } from '../../../Switch';
import {
  ActionData,
  FileBrowserSelectionType,
  PathType,
  S3Path
} from '@convergence/types';
import { useState } from 'react';
import { LabFileBrowser } from '../../../lab-file-browser';
import { useStateLocalStorage } from '../../../../hooks/useStateLocalStorage';

export type PathActionInputType = 'value' | 'code' | 'pattern';

export interface ActionInputSelectorProps {
  valueSchema: PathType;
  actionData: ActionData;
  setActionData: (data: ActionData) => void;
}

const defaultCode = `
# Write your code here to dynamically select a file for your analysis.
"""
    Process a list of lab file paths.
    
    Args:
        paths: A list of strings representing lab file paths.
               Each path should follow the format 'my-lab://some/file/path'.
               Example: ['my-lab://data/input.csv', 'my-lab://models/model.pkl']
    Returns:
        str: a list of lab file paths to be used in the analysis.
    """
def getValue(input):
    return ["axiotl-research://myfile.txt"]
`;

export function ActionPathInputSelector(props: ActionInputSelectorProps) {
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [fileBrowserState, setFileBrowserState] = useStateLocalStorage(
    'fileBrowser',
    {
      path: [] as string[],
      bucketId: null as number | null
    }
  );
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const value =
    props.actionData.type === 'value'
      ? (props.actionData.value as S3Path[])
      : [];
  const pattern =
    props.actionData.type === 'pattern' ? props.actionData.pattern : '';
  return (
    <div className="flex gap-2 items-center px-2">
      <div className="flex items-center gap-2">
        <Switch value={props.actionData.type}>
          <Case value="value">
            <Button
              onClick={() => {
                setShowFileBrowser(!showFileBrowser);
              }}
            >
              Choose File
            </Button>
          </Case>
          <Case value="pattern">
            <Input
              type="text"
              placeholder="**/*.txt"
              value={pattern}
              onChange={(e) => {
                props.setActionData({
                  ...props.actionData,
                  type: 'pattern',
                  pattern: e.currentTarget.value
                });
              }}
            />
          </Case>
          <Case value="code">
            <Button
              onClick={() => {
                setShowCodeEditor(true);
              }}
            >
              Edit
            </Button>
          </Case>
        </Switch>
        <Select
          value={props.actionData.type}
          onChange={(v) => {
            const newType = v.currentTarget.value as ActionData['type'];
            switch (newType) {
              case 'pattern':
                props.setActionData({
                  type: 'pattern',
                  pattern: ''
                });
                break;
              case 'code':
                props.setActionData({
                  type: 'code',
                  codeBase64: defaultCode
                });
                break;
              case 'value':
                props.setActionData({
                  type: 'value',
                  value: []
                });
                break;
            }
          }}
        >
          <option value="value">Value</option>
          <option value="pattern">Pattern</option>
          <option value="code">Code</option>
        </Select>
        {/* {error && <p className="text-red-600">{error.message}</p>} */}
        {/* {value && */}
        {/*   value.length > 0 && */}
        {/*   value.map((v, i) => ( */}
        {/*     <SelectedPath */}
        {/*       path={v.path} */}
        {/*       bucketId={v.bucketId ?? 0} */}
        {/*       isFolder={v.isFolder} */}
        {/*       key={`selected-path-${v.path}`} */}
        {/*       onDelete={() => { */}
        {/*         const newValue = [...(value ?? [])]; */}
        {/*         newValue.splice(i, 1); */}
        {/*         props.onChange(props.propertyPath, newValue); */}
        {/*       }} */}
        {/*     /> */}
        {/*   ))} */}
      </div>
      <Modal
        visible={showCodeEditor}
        onClose={() => setShowCodeEditor(false)}
        className=""
      >
        <Editor language="python" defaultValue={defaultCode} />
      </Modal>
      <Modal
        visible={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        className=""
      >
        <div className="p-4 h-full overflow-hidden flex flex-col">
          <LabFileBrowser
            path={fileBrowserState.path}
            setPath={(path) =>
              setFileBrowserState({ ...fileBrowserState, path })
            }
            bucketId={fileBrowserState.bucketId ?? undefined}
            setBucketId={(bucketId) =>
              setFileBrowserState({ ...fileBrowserState, bucketId })
            }
            selection={value ?? []}
            pathType={FileBrowserSelectionType.ALL}
            onSelectionChange={(selection) => {
              if (props.valueSchema._regexp) {
                const r = new RegExp(props.valueSchema._regexp);
                const filteredSelection = selection.filter((p) =>
                  r.test(p.path)
                );
                props.setActionData({
                  ...props.actionData,
                  type: 'value',
                  value: filteredSelection ?? []
                });
              } else {
                props.setActionData({
                  ...props.actionData,
                  type: 'value',
                  value: selection ?? []
                });
              }
            }}
          />
          <Button className="mt-4" onClick={() => setShowFileBrowser(false)}>
            Select
          </Button>
        </div>
      </Modal>
    </div>
  );
}
